import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { fa } from "../../core/format";
import Modal from "../../components/Modal";
import FileViewer from "../../components/FileViewer";

const OK = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
const MAX_MB = 5;
const sizeText = (b) => (b >= 1048576 ? `${fa((b / 1048576).toFixed(1))} مگابایت` : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`);
const icon = (m) => (m === "application/pdf" ? "fa-regular fa-file-pdf" : m ? "fa-regular fa-image" : "fa-regular fa-file");

export default function PledgeFiles({ pledge, admin, onClose, onChanged }) {
  const { toast, confirm } = useApp();
  const inputRef = useRef(null);
  const catRef = useRef("intro");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/pledges/${pledge.id}/files`); setItems(data.items); }
    catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [pledge.id, toast]);
  useEffect(() => { load(); }, [load]);

  const pick = (cat) => { catRef.current = cat; inputRef.current.click(); };

  const onFiles = async (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = "";
    if (!picked.length) return;
    const bad = picked.find((f) => !OK.includes(f.type) || f.size > MAX_MB * 1024 * 1024);
    if (bad) return toast(`«${bad.name}»: فقط PDF یا تصویر تا ${fa(MAX_MB)} مگابایت مجاز است`, "err");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", catRef.current);
      picked.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/pledges/${pledge.id}/files`, fd, { timeout: 180000 });
      setItems(data.items);
      toast("فایل‌ها ذخیره شد");
      onChanged?.();
    } catch (er) { toast(errMsg(er), "err"); } finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!(await confirm(`فایل «${f.name}» حذف شود؟`))) return;
    try { await api.delete(`/pledges/${pledge.id}/files/${f.id}`); setItems((l) => l.filter((x) => x.id !== f.id)); }
    catch (er) { toast(errMsg(er), "err"); }
  };

  const row = (key, name, mime, size, path, f) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", background: "var(--card)" }}>
      <i className={icon(mime)} style={{ color: "var(--primary)" }} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>{name}</span>
      {size ? <small className="hint" style={{ margin: 0 }}>{sizeText(size)}</small> : null}
      <button type="button" className="icon-btn" title="مشاهده" onClick={() => setViewer({ path, name })}><i className="fa-regular fa-eye" /></button>
      {f && admin && <button type="button" className="icon-btn danger" title="حذف" onClick={() => remove(f)}><i className="fa-solid fa-trash" /></button>}
    </div>
  );

  const section = (title, cat, main) => {
    const list = items.filter((i) => i.category === cat);
    return (
      <div className="field">
        <div className="box-head" style={{ marginBottom: 8 }}>
          <h3 style={{ fontSize: 14 }}>{title}</h3>
          <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => pick(cat)}>
            <i className="fa-solid fa-plus" /> {busy ? "در حال ارسال..." : "افزودن فایل"}
          </button>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {main && row("main", "فایل اصلی تعهدنامه", "", 0, `/pledges/${pledge.id}/file`, null)}
          {list.map((f) => row(f.id, f.name, f.mime, f.size, `/pledges/${pledge.id}/files/${f.id}`, f))}
          {!list.length && !main && <div className="hint">فایلی ثبت نشده است.</div>}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal wide title={`فایل‌های تعهدنامه — ${pledge.firstName} ${pledge.lastName}`}
        onClose={() => !busy && !viewer && onClose()}
        footer={<button className="btn btn-ghost" onClick={onClose} disabled={busy}>بستن</button>}>
        {loading ? <div className="empty"><div className="spinner" style={{ margin: "0 auto" }} /></div> : (
          <>
            {section("معرفی‌نامه", "intro", false)}
            {section("فایل‌های تعهدنامه", "pledge", !!pledge.hasFile)}
            <div className="hint">هر بخش تا {fa(10)} فایل و هر فایل حداکثر {fa(MAX_MB)} مگابایت؛ انتخاب چندفایله مجاز است.{admin ? "" : " حذف فایل فقط توسط ادمین انجام می‌شود."}</div>
            <input ref={inputRef} type="file" multiple hidden accept="application/pdf,image/png,image/jpeg,image/webp" onChange={onFiles} />
          </>
        )}
      </Modal>
      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
    </>
  );
}